"use client";

import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/store/useAuthStore";
import { getInteraction, interactionKey, replaceInteraction, setInteraction, useInteraction } from "@/features/interactions/store";
import { syncInteractionCache } from "@/features/interactions/cache";
import { toggleWatchlist, type WatchlistableModel } from "./api";
type UseWatchlistToggleOptions = {
  modelType: WatchlistableModel;
  /** Numeric for most models; drops identify by slug (they have no numeric id). */
  modelId?: number | string;
  /** `current_user.watchlist` from the API — never a `watchlist`/`follow` container. */
  initialWatchlisted?: boolean;
  successMessage?: {
    added: string;
    removed: string;
  };
  errorMessage?: string;
};
export function useWatchlistToggle({
  modelType,
  modelId,
  initialWatchlisted = false,
  successMessage,
  errorMessage
}: UseWatchlistToggleOptions) {
  const {
    user,
    openLoginModal
  } = useAuthStore();
  const queryClient = useQueryClient();
  const key = interactionKey(modelType, modelId);
  const shared = useInteraction(key);

  // Same precedence rule as useLikeToggle: the optimistic value only outranks
  // the prop while the server is still reporting the pre-toggle one.
  const hasFreshServerValue = shared?.watchlistBase !== undefined && shared.watchlistBase !== initialWatchlisted;
  const watchlisted = shared?.watchlist != null && !hasFreshServerValue ? shared.watchlist : initialWatchlisted;
  const isPending = shared?.watchlistPending ?? false;
  const toggle = async () => {
    if (!user) {
      openLoginModal();
      return;
    }
    if (!key || isPending) return;
    const previous = getInteraction(key);
    const next = !watchlisted;
    setInteraction(key, {
      watchlist: next,
      watchlistBase: initialWatchlisted,
      watchlistPending: true
    });
    try {
      await toggleWatchlist(modelType, modelId!, next);
      setInteraction(key, {
        watchlistPending: false
      });
      syncInteractionCache(queryClient, modelType, modelId!, {
        watchlist: next
      });
      // Toast is cosmetic — the state above is already committed without it.
      if (successMessage) {
        toast.success(next ? successMessage.added : successMessage.removed);
      }
    } catch {
      replaceInteraction(key, {
        ...getInteraction(key),
        watchlist: previous?.watchlist,
        watchlistBase: previous?.watchlistBase,
        watchlistPending: false
      });
      toast.error(errorMessage || "Failed to update watchlist");
    }
  };
  return {
    watchlisted,
    isPending,
    toggle
  };
}